// FILE: apps/admin/src/pages/Alerts.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Operator Alert Center & Runbook page displaying live system/finance/security alerts, interactive 25-kind runbook instructions, acknowledgement controls, and Telegram DM test alert trigger.
//   SCOPE: Alerts component.
//   DEPENDS: ../alert-runbook.js, ../api.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Alerts - Operator Alert Center and Runbook reference page
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ALERT_RUNBOOK_CATALOG, getAlertRunbook } from '../alert-runbook.js';
import { api, dt, num } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { useToast } from '../toast.js';

interface AlertItem {
  id: string;
  kind: string;
  severity: 'CRITICAL' | 'HIGH' | 'WARN' | 'INFO';
  category: string;
  title: string;
  body: string;
  deepLinkPath: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
}

interface AlertsResponse {
  items: AlertItem[];
  unreadCount: number;
}

// START_CONTRACT: Alerts
//   PURPOSE: Render the Operator Alert Center and interactive Runbook catalog.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls GET /admin/alerts, POST /admin/alerts/:id/ack, POST /admin/alerts/ack-all, and POST /admin/alerts/test
//   LINKS: M-ADMIN
// END_CONTRACT: Alerts
export function Alerts() {
  // START_BLOCK_ALERTS
  const qc = useQueryClient();
  const toast = useToast();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedKind, setSelectedKind] = useState<string>('tbank.session_expired');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-alerts-page', unreadOnly],
    queryFn: () =>
      api.get<AlertsResponse>(`/admin/alerts?unreadOnly=${unreadOnly ? 'true' : 'false'}`),
    refetchInterval: 12000,
  });

  const ackMut = useMutation({
    mutationFn: (id: string) => api.post(`/admin/alerts/${id}/ack`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-alerts-page'] });
      void qc.invalidateQueries({ queryKey: ['admin-alerts'] });
    },
  });

  const ackAllMut = useMutation({
    mutationFn: () => api.post('/admin/alerts/ack-all'),
    onSuccess: () => {
      toast('Все уведомления отмечены как прочитанные');
      void qc.invalidateQueries({ queryKey: ['admin-alerts-page'] });
      void qc.invalidateQueries({ queryKey: ['admin-alerts'] });
    },
  });

  const testAlertMut = useMutation({
    mutationFn: () => api.post('/admin/alerts/test'),
    onSuccess: () => {
      toast('Тестовый алерт создан и отправлен операторам в Telegram');
      void qc.invalidateQueries({ queryKey: ['admin-alerts-page'] });
      void qc.invalidateQueries({ queryKey: ['admin-alerts'] });
    },
  });

  const activeRunbook = getAlertRunbook(selectedKind);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Центр алертов и Runbook (25 сценариев)</h1>
          <div className="page-subtitle">
            Оперативные инциденты платформы с пошаговыми регламентами устранения · Непрочитанных:{' '}
            <strong>{num(data?.unreadCount ?? 0)}</strong>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn sm ghost"
            disabled={testAlertMut.isPending}
            onClick={() => testAlertMut.mutate()}
          >
            <AdminIcon name="bell" size={13} />
            <span>Тестовый алерт</span>
          </button>
          <button
            type="button"
            className="btn sm primary"
            disabled={ackAllMut.isPending || !data?.unreadCount}
            onClick={() => ackAllMut.mutate()}
          >
            <AdminIcon name="check" size={13} />
            <span>Прочитать все</span>
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button
            type="button"
            className={`tab-btn ${!unreadOnly ? 'active' : ''}`}
            onClick={() => setUnreadOnly(false)}
          >
            Все события
          </button>
          <button
            type="button"
            className={`tab-btn ${unreadOnly ? 'active' : ''}`}
            onClick={() => setUnreadOnly(true)}
          >
            Только непрочитанные ({num(data?.unreadCount ?? 0)})
          </button>
        </div>
      </div>

      <div className="grid cols-2" style={{ alignItems: 'start' }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {isLoading ? (
            <div className="muted" style={{ padding: 20 }}>Загрузка ленты алертов…</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Уровень</th>
                    <th>Событие</th>
                    <th>Время</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items ?? []).map((a) => (
                    <tr
                      key={a.id}
                      style={{
                        cursor: 'pointer',
                        opacity: a.acknowledgedAt ? 0.65 : 1,
                      }}
                      onClick={() => setSelectedKind(a.kind)}
                    >
                      <td>
                        <span className={`chip ${a.severity}`}>{a.severity}</span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{a.title}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{a.body}</div>
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>{dt(a.createdAt)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {!a.acknowledgedAt && (
                          <button
                            type="button"
                            className="btn sm ghost"
                            onClick={() => ackMut.mutate(a.id)}
                          >
                            ACK
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!data?.items?.length && (
                    <tr>
                      <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                        Активных инцидентов нет — все системы в норме
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <h2 style={{ margin: 0 }}>Инструкция оператора (Runbook)</h2>
            <span className={`chip ${activeRunbook.severity}`}>{activeRunbook.severity}</span>
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
            {activeRunbook.title}
          </div>
          <div className="mono muted" style={{ fontSize: 12, marginBottom: 14 }}>
            Код события: {activeRunbook.kind}
          </div>

          <div className="stepup-box">
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>
              Пошаговый план действий:
            </div>
            <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {activeRunbook.steps.map((step, idx) => (
                <li key={idx} style={{ fontSize: 13 }}>{step}</li>
              ))}
            </ol>
          </div>

          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 18 }}>
            <Link to={activeRunbook.deepLinkPath} className="btn primary">
              {activeRunbook.actionLabel} →
            </Link>
          </div>

          <h2>Справочник типовых сценариев</h2>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {Object.values(ALERT_RUNBOOK_CATALOG).map((item) => (
              <button
                key={item.kind}
                type="button"
                className={`btn sm ${selectedKind === item.kind ? 'primary' : 'ghost'}`}
                onClick={() => setSelectedKind(item.kind)}
              >
                {item.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
  // END_BLOCK_ALERTS
}
